/**
 * NPI Governance Solutions — Branded Email Template Renderer
 *
 * Pure function: no side effects, no imports.
 * All CSS is inline for email-client compatibility.
 * Uses email-safe nested tables; border-radius is cosmetic-only (Outlook ignores it).
 */

const FONT = "'IBM Plex Sans', Inter, Arial, sans-serif";

const C = {
  primaryBlue: '#275372',
  darkBlue:    '#214667',
  accentRed:   '#DD3F51',
  bg:          '#F4F4F6',
  cardBg:      '#FFFFFF',
  border:      '#EDECEF',
  textBody:    '#1F2937',
  textSub:     '#687385',
  textFoot:    '#9FA5B2',
};

const ESC_MAP = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ESC_MAP[c]);

const autoLink = (text) =>
  text.replace(
    /(https?:\/\/[^\s<>"'()[\]]+)/g,
    (url) => `<a href="${esc(url)}" style="color:${C.accentRed};text-decoration:underline;">${esc(url)}</a>`
  );

const normaliseBody = (raw) => {
  if (!raw) return '';
  const hasTag = /<\/?[a-z][\s\S]*>/i.test(raw);
  if (hasTag) return raw;
  return esc(raw)
    .split(/\n\s*\n/)
    .filter(Boolean)
    .map((p) => `<p style="margin:0 0 12px;">${autoLink(p.replace(/\n/g, '<br>'))}</p>`)
    .join('');
};

/**
 * @param {object} opts
 * @param {string}   opts.orgName       Organisation name — plain text
 * @param {string}   opts.appName       Application name — plain text
 * @param {string}   opts.senderEmail   Shown in footer — plain text
 * @param {string}   opts.portalUrl     Portal home URL
 * @param {string}   opts.npiLogoUrl    Absolute URL to NPI logo (optional)
 * @param {string}   opts.appLogoUrl    Absolute URL to app logo (optional)
 * @param {string}   opts.heading       Card heading — plain text (optional)
 * @param {string}   opts.subtitle      Subheading beneath heading — plain text (optional)
 * @param {string}   opts.bodyHtml      Main content — HTML or plain text
 * @param {string}   opts.ctaText       CTA button label — plain text (optional)
 * @param {string}   opts.ctaUrl        CTA button href (optional)
 * @param {Array}    opts.resourceLinks [{label, url}] — shown as "Helpful links" list (optional)
 * @returns {string} Complete HTML document
 */
export function renderNPIEmail({
  orgName       = 'NPI Governance Solutions',
  appName       = 'Access70 LMS',
  senderEmail   = '',
  portalUrl     = 'https://lms-access70.web.app',
  npiLogoUrl    = '',
  appLogoUrl    = '',
  heading       = '',
  subtitle      = '',
  bodyHtml      = '',
  ctaText       = '',
  ctaUrl        = '',
  resourceLinks = [],
} = {}) {

  /* ── Logos ─────────────────────────────────────────────────────────────── */
  const logoCells = [
    npiLogoUrl && `<td style="padding-right:12px;vertical-align:middle;"><img src="${esc(npiLogoUrl)}" alt="NPI" height="36" style="display:block;border:0;height:36px;max-height:36px;"></td>`,
    appLogoUrl && `<td style="vertical-align:middle;"><img src="${esc(appLogoUrl)}" alt="${esc(appName)}" height="36" style="display:block;border:0;height:36px;max-height:36px;"></td>`,
  ].filter(Boolean);

  const logoRow = logoCells.length ? `
          <tr>
            <td align="center" style="padding:20px 20px 0;">
              <table border="0" cellpadding="0" cellspacing="0" role="presentation"><tr>${logoCells.join('')}</tr></table>
            </td>
          </tr>` : '';

  /* ── Heading ────────────────────────────────────────────────────────────── */
  const headingTopPad = logoCells.length ? '12px' : '22px';
  const headingRow = heading ? `
          <tr>
            <td style="padding:${headingTopPad} 20px 4px;">
              <h1 style="margin:0;font-family:${FONT};font-size:22px;font-weight:700;color:${C.primaryBlue};line-height:1.3;">${esc(heading)}</h1>
            </td>
          </tr>` : '';

  /* ── Subtitle ───────────────────────────────────────────────────────────── */
  const subtitleRow = subtitle ? `
          <tr>
            <td style="padding:0 20px 16px;">
              <p style="margin:0;font-family:${FONT};font-size:15px;color:${C.textSub};line-height:1.5;">${esc(subtitle)}</p>
            </td>
          </tr>` : '';

  /* ── Body ───────────────────────────────────────────────────────────────── */
  const hasAbove = logoCells.length || heading || subtitle;
  const bodyTopPad = hasAbove ? '8px' : '22px';
  const bodyRow = bodyHtml ? `
          <tr>
            <td style="padding:${bodyTopPad} 20px 12px;font-family:${FONT};font-size:15px;line-height:1.55;color:${C.textBody};">
              ${normaliseBody(bodyHtml)}
            </td>
          </tr>` : '';

  /* ── CTA ────────────────────────────────────────────────────────────────── */
  const ctaRow = (ctaText && ctaUrl) ? `
          <tr>
            <td style="padding:8px 20px 20px;">
              <table border="0" cellpadding="0" cellspacing="0" role="presentation">
                <tr>
                  <td align="center" bgcolor="${C.accentRed}" style="border-radius:8px;background-color:${C.accentRed};">
                    <a href="${esc(ctaUrl)}" target="_blank" style="display:inline-block;padding:10px 16px;font-family:${FONT};font-size:15px;font-weight:700;color:#FFFFFF;text-decoration:none;border-radius:8px;">${esc(ctaText)}</a>
                  </td>
                </tr>
              </table>
            </td>
          </tr>` : '';

  /* ── Resource links ─────────────────────────────────────────────────────── */
  const links = (resourceLinks || []).filter((r) => r && r.label && r.url);
  const linksRow = links.length ? `
          <tr>
            <td style="padding:4px 20px 20px;">
              <p style="margin:0 0 8px;font-family:${FONT};font-size:14px;font-weight:700;color:${C.primaryBlue};">Helpful links</p>
              <ul style="margin:0;padding:0 0 0 18px;">
                ${links.map(({ label, url }) =>
                  `<li style="margin-bottom:6px;font-family:${FONT};font-size:14px;line-height:1.5;"><a href="${esc(url)}" style="color:${C.accentRed};text-decoration:underline;">${esc(label)}</a></li>`
                ).join('\n                ')}
              </ul>
            </td>
          </tr>` : '';

  /* ── Footer ─────────────────────────────────────────────────────────────── */
  const footerParts = [
    `Sent by <strong style="font-weight:600;">${esc(appName)}</strong>`,
    senderEmail
      ? `<a href="mailto:${esc(senderEmail)}" style="color:${C.textFoot};text-decoration:none;">${esc(senderEmail)}</a>`
      : null,
  ].filter(Boolean);

  /* ── Document ───────────────────────────────────────────────────────────── */
  return `<!DOCTYPE html>
<html lang="en" xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width,initial-scale=1">
  <meta http-equiv="X-UA-Compatible" content="IE=edge">
  <meta name="x-apple-disable-message-reformatting">
  <title>${esc(heading || appName)}</title>
  <!--[if mso]>
  <noscript><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch></o:OfficeDocumentSettings></xml></noscript>
  <![endif]-->
  <link href="https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;600;700&display=swap" rel="stylesheet">
  <style>
    @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;600;700&display=swap');
    body, table, td, a { -webkit-text-size-adjust: 100%; -ms-text-size-adjust: 100%; }
    table, td { mso-table-lspace: 0pt; mso-table-rspace: 0pt; border-collapse: collapse; }
    img { border: 0; outline: none; text-decoration: none; }
    body { margin: 0 !important; padding: 0 !important; background-color: ${C.bg}; }
  </style>
</head>
<body style="margin:0;padding:0;background-color:${C.bg};">
  <!-- Outer wrapper -->
  <table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0" bgcolor="${C.bg}" style="background-color:${C.bg};">
    <tr>
      <td align="center" valign="top" style="padding:24px 16px;">
        <!--[if (gte mso 9)|(IE)]><table width="620" border="0" cellpadding="0" cellspacing="0" align="center"><tr><td><![endif]-->
        <!-- Card -->
        <table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0" align="center" style="max-width:620px;background-color:${C.cardBg};border:1px solid ${C.border};border-radius:12px;overflow:hidden;">

          <!-- ═══ HEADER ═══ -->
          <tr>
            <td bgcolor="${C.darkBlue}" style="background-color:${C.darkBlue};padding:16px 20px;border-radius:12px 12px 0 0;">
              <p style="margin:0;font-family:${FONT};font-size:13px;font-weight:700;color:rgba(255,255,255,0.9);letter-spacing:0.08em;text-transform:uppercase;line-height:1.2;">${esc(orgName)}</p>
              <p style="margin:4px 0 0;font-family:${FONT};font-size:20px;font-weight:700;color:#FFFFFF;line-height:1.2;">${esc(appName)}</p>
            </td>
          </tr>

          <!-- ═══ CONTENT ═══ -->
          ${logoRow}
          ${headingRow}
          ${subtitleRow}
          ${bodyRow}
          ${ctaRow}
          ${linksRow}

          <!-- ═══ FOOTER ═══ -->
          <tr>
            <td style="border-top:1px solid ${C.border};padding:14px 20px;">
              <p style="margin:0;font-family:${FONT};font-size:12px;color:${C.textFoot};line-height:1.4;">${footerParts.join(' &bull; ')}</p>
            </td>
          </tr>

        </table>
        <!--[if (gte mso 9)|(IE)]></td></tr></table><![endif]-->
      </td>
    </tr>
  </table>
</body>
</html>`;
}
